import React from 'react';

import { Link } from '@mui/material';
import { isString } from 'es-toolkit';

import {
  ApolloClientProvider,
  type ApolloRequestErrorHandler,
  type ApolloRequestSuccessHandler,
} from '@hrlogics/react-apollo-v3';
import { useAuthCredentials } from '@hrlogics/react-auth';

import { APP_API_ENDPOINT, IS_PRODUCTION_BUILD } from 'src/config';
import { useNotification } from 'src/hooks/app';

import { createApolloCacheConfig } from './apolloCacheConfig';

export const ApolloProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { bearerToken, resetAuthCredentials } = useAuthCredentials();

  const { spawnNotification, apolloContextValues } = useNotification();

  const onRequestSuccess: ApolloRequestSuccessHandler = React.useCallback(
    ({ operation }) => {
      const snackbarSuccessMessage = operation.getContext()[apolloContextValues.success];
      if (isString(snackbarSuccessMessage)) {
        spawnNotification(snackbarSuccessMessage, 'success');
      }
    },
    [apolloContextValues.success, spawnNotification],
  );

  const onRequestError: ApolloRequestErrorHandler = React.useCallback(
    ({ networkError, graphQLErrors, operation }) => {
      const snackbarErrorMessageTitle = operation.getContext()[apolloContextValues.error];

      if (networkError && 'statusCode' in networkError && 'result' in networkError) {
        if (
          networkError.statusCode === 403 &&
          networkError.result === 'Invalid or expired auth token'
        ) {
          resetAuthCredentials();
        }
      }

      if (graphQLErrors) {
        for (const error of graphQLErrors) {
          console.error(error);
          spawnNotification(
            buildErrorMessage(
              error.message,
              error.extensions?.docUrl,
              error.extensions?.docLearnMoreText,
            ),
            'error',
            snackbarErrorMessageTitle ?? undefined,
          );
        }
      }
    },
    [apolloContextValues.error, resetAuthCredentials, spawnNotification],
  );

  return (
    <ApolloClientProvider
      endpointUrl={APP_API_ENDPOINT}
      bearerToken={bearerToken}
      cacheConfig={apolloCacheConfig}
      onRequestSuccess={onRequestSuccess}
      onRequestError={onRequestError}
      options={apolloClientOptions}
      httpLinkEnableBatchingCondition={() => true}
    >
      {children}
    </ApolloClientProvider>
  );
};

/**
 * An error may name somewhere to read about it, in the `docUrl` extension. The message says what
 * went wrong and the link says what to do next, which is worth offering whenever a resolver knows
 * of such a page. A resolver that can put it better than "Learn more" says so in
 * `docLearnMoreText`. Every other error is plain text.
 */
const buildErrorMessage = (
  message: string,
  docUrl: unknown,
  learnMoreText: unknown,
): React.ReactNode =>
  isString(docUrl) ? (
    <React.Fragment>
      {message}{' '}
      <Link href={docUrl} target="_blank" rel="noopener" underline="always">
        {isString(learnMoreText) ? learnMoreText : 'Learn more'}
      </Link>
    </React.Fragment>
  ) : (
    message
  );

const apolloClientOptions = {
  connectToDevTools: !IS_PRODUCTION_BUILD,
};

const apolloCacheConfig = createApolloCacheConfig();
